import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { Chips, Field } from "./Chips";
import { PaymentChoice } from "./PaymentChoice";

const sel = "w-full border border-input bg-white h-10 px-3 text-sm mt-1";
const BLANK = { category: "FREE", price_key: "", pay_now: true, organisation: "", name: "", job_title: "", email: "", phone: "", country: "", operating_countries: "",
  website: "", org_type: "", company_size: "", commodities: [], services: [], services_detail: "", opportunities: [], opportunities_detail: "", events: [],
  objectives: "", profile: "", newsletter_optin: true };
const ORG_TYPES = ["Private company", "Cooperative / farmer group", "Government / public body", "Embassy / high commission", "Trade body / association", "NGO / development agency", "Investor / fund", "Media", "Individual / consultant"];
const SIZES = ["Just me", "2–10", "11–50", "51–250", "251+"];

export const AgapRegister = ({ data }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const val = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const cat = data.categories.find((c) => c.code === f.category);
  const setCategory = (code) => {
    const c = data.categories.find((x) => x.code === code);
    setF((p) => ({ ...p, category: code, price_key: c?.options[0]?.key || "" }));
  };

  useEffect(() => {
    const h = (e) => setCategory(e.detail.code);
    window.addEventListener("agap-pick", h);
    return () => window.removeEventListener("agap-pick", h);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!f.services.length) return toast.error("Pick at least one service you offer");
    if (!f.opportunities.length) return toast.error("Pick at least one opportunity you are looking for");
    setBusy(true);
    try {
      const { data: r } = await api.post("/agap/register", { ...f, origin_url: window.location.origin });
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      setDone(f.category); setTimeout(() => document.getElementById("agap-register")?.scrollIntoView({ behavior: "smooth" }), 50); toast.success("Registration received. Check your inbox.");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <section id="agap-register" className="bg-[#1F2328] scroll-mt-20" data-testid="agap-register">
      <div className="max-w-5xl mx-auto px-5 py-16 lg:py-24">
        <p className="overline text-[#A6D26B]">Register with AGAP</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 text-white">Tell us who you are and what you are looking for</h2>
        <p className="text-white/70 mt-3 max-w-2xl text-sm">Every registration builds the AGAP Africa partner database, so we can match you with the right buyers, investors and projects.</p>
        <div className="mt-10 bg-white p-6 sm:p-10">
          {done ? (
            <div data-testid="agap-register-done">
              <CheckCircle2 size={36} className="text-[#5E9420]" />
              <p className="font-display text-2xl font-bold mt-4">Thank you. You're in.</p>
              <p className="text-[#575757] mt-2">{done === "FREE" ? "You'll start receiving Africa Brew Brief intelligence by email, and you're now part of the AGAP partner database." : "The AGAP team will review your registration and reply from admin@africabrewbrief.com within two working days."}</p>
              <button onClick={() => { setDone(null); setF(BLANK); }} data-testid="agap-register-another" className="mt-6 text-sm underline">Register another organisation</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="agap-register-form">
              <div>
                <p className="overline text-[#2F343B]">1 · Registration type</p>
                <Field label="Category" className="mt-3">
                  <select data-testid="agap-form-category" value={f.category} onChange={(e) => setCategory(e.target.value)} className={sel}>
                    <option value="FREE">Free · News & intelligence access only</option>
                    {data.groups.map((g) => (
                      <optgroup key={g.code} label={`${g.code} · ${g.name}`}>
                        {data.categories.filter((c) => c.group === g.code).map((c) => <option key={c.code} value={c.code}>{c.code}. {c.name} ({c.fee.split(" · ")[0]})</option>)}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                {cat && <PaymentChoice cat={cat} f={f} setF={setF} />}
              </div>
              <div>
                <p className="overline text-[#2F343B]">2 · Company details</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <Input data-testid="agap-form-organisation" required placeholder="Organisation / company name *" value={f.organisation} onChange={set("organisation")} className="rounded-none" />
                  <Input data-testid="agap-form-website" placeholder="Website or LinkedIn" value={f.website} onChange={set("website")} className="rounded-none" />
                  <Input data-testid="agap-form-name" required placeholder="Contact person *" value={f.name} onChange={set("name")} className="rounded-none" />
                  <Input data-testid="agap-form-job-title" placeholder="Job title" value={f.job_title} onChange={set("job_title")} className="rounded-none" />
                  <Input data-testid="agap-form-email" required type="email" placeholder="Email *" value={f.email} onChange={set("email")} className="rounded-none" />
                  <Input data-testid="agap-form-phone" placeholder="Phone / WhatsApp" value={f.phone} onChange={set("phone")} className="rounded-none" />
                  <Input data-testid="agap-form-country" required placeholder="Country of registration *" value={f.country} onChange={set("country")} className="rounded-none" />
                  <Input data-testid="agap-form-operating" placeholder="Countries you operate in" value={f.operating_countries} onChange={set("operating_countries")} className="rounded-none" />
                  <select data-testid="agap-form-org-type" value={f.org_type} onChange={set("org_type")} className={sel.replace(" mt-1", "")}>
                    <option value="">Organisation type</option>{ORG_TYPES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <select data-testid="agap-form-size" value={f.company_size} onChange={set("company_size")} className={sel.replace(" mt-1", "")}>
                    <option value="">Team size</option>{SIZES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <Field label="Sectors & commodities" className="mt-5"><Chips items={data.commodities} value={f.commodities} onChange={val("commodities")} testid="agap-form-commodities" /></Field>
              </div>
              <div>
                <p className="overline text-[#2F343B]">3 · What you offer</p>
                <Field label="Services you offer *" className="mt-3"><Chips items={data.services} value={f.services} onChange={val("services")} testid="agap-form-services" /></Field>
                <Textarea data-testid="agap-form-services-detail" placeholder="Describe your products or services in a few lines" value={f.services_detail} onChange={set("services_detail")} className="rounded-none mt-4 min-h-[90px]" />
              </div>
              <div>
                <p className="overline text-[#2F343B]">4 · What you're looking for in Africa trade & investment</p>
                <Field label="Opportunities you're seeking *" className="mt-3"><Chips items={data.opportunities} value={f.opportunities} onChange={val("opportunities")} testid="agap-form-opportunities" /></Field>
                <Textarea data-testid="agap-form-opportunities-detail" placeholder="Which markets, products, deal sizes or partners are you looking for?" value={f.opportunities_detail} onChange={set("opportunities_detail")} className="rounded-none mt-4 min-h-[90px]" />
                <Field label="Events of interest" className="mt-5"><Chips items={data.events} value={f.events} onChange={val("events")} testid="agap-form-events" /></Field>
                {f.category !== "FREE" && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-5">
                    <Textarea data-testid="agap-form-objectives" placeholder="Your objectives for partnering" value={f.objectives} onChange={set("objectives")} className="rounded-none min-h-[90px]" />
                    <Textarea data-testid="agap-form-profile" placeholder="Short profile for the AGAA directory / event materials" value={f.profile} onChange={set("profile")} className="rounded-none min-h-[90px]" />
                  </div>
                )}
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" data-testid="agap-form-newsletter" checked={f.category === "FREE" || f.newsletter_optin} disabled={f.category === "FREE"} onChange={(e) => setF({ ...f, newsletter_optin: e.target.checked })} className="mt-1 accent-[#5E9420]" />
                Send me The Tuesday Roast and Africa Brew Brief intelligence by email. Unsubscribe any time.
              </label>
              <button disabled={busy} data-testid="agap-form-submit" className="w-full bg-[#5E9420] hover:bg-[#4C7A18] text-white h-12 font-semibold disabled:opacity-60 transition-colors">
                {busy ? "Sending…" : f.category === "FREE" ? "Join free" : cat?.options.length && f.pay_now ? "Continue to secure payment" : "Submit registration"}
              </button>
              <ul className="text-[11px] text-[#8A8A8A] space-y-0.5">{data.terms.map((t) => <li key={t}>{t}</li>)}</ul>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
